/**
 * useProfile — React hook for fetching and saving the user's HealthProfile.
 *
 * Calls GET /me/profile (to load) and PUT /me/profile (to save).
 * Pattern identical to useTracking (container/presentational split, ADR-3).
 *
 * Security:
 *   - Authorization header is set to "Bearer {idToken}"; token is fresh per call.
 *   - uid is NEVER stored in state or sent in the body — it comes from the token.
 *   - Error messages are generic (PII-safe); no raw server errors shown to user.
 *   - No dangerouslySetInnerHTML usage.
 *
 * Design: ADR-3 (profile-form-ui design §Container/presentational).
 */

import { useState, useEffect, useCallback, useRef } from "react";
import type { HealthProfile } from "@autodieta/shared";
import { useAuth } from "../auth/AuthContext.js";

// ---------------------------------------------------------------------------
// Hook return type
// ---------------------------------------------------------------------------

export interface UseProfileResult {
  /** The user's current HealthProfile, or null if none exists yet (404). */
  profile: Partial<HealthProfile> | null;
  /** True while a GET or PUT request is in flight. */
  loading: boolean;
  /** Generic error message, if the last request failed. Undefined on success. */
  error?: string;
  /** True immediately after a successful save (reset on next action). */
  saved: boolean;
  /** Save a partial profile via PUT /me/profile. */
  save(partial: Partial<HealthProfile>): Promise<void>;
  /** Re-fetch the profile from the server. */
  refetch(): Promise<void>;
}

// ---------------------------------------------------------------------------
// useProfile
// ---------------------------------------------------------------------------

/**
 * Fetches and persists the user's HealthProfile via the Worker.
 *
 * @param workerUrl - Base URL of the Cloudflare Worker.
 */
export function useProfile(workerUrl: string): UseProfileResult {
  const { getIdToken } = useAuth();

  const [profile, setProfile] = useState<Partial<HealthProfile> | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | undefined>(undefined);
  const [saved, setSaved] = useState(false);

  // Stable ref so callbacks don't re-create on every render
  const workerUrlRef = useRef(workerUrl);
  workerUrlRef.current = workerUrl;

  const refetch = useCallback(async () => {
    const token = await getIdToken();
    if (!token) {
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(undefined);

    try {
      const res = await fetch(`${workerUrlRef.current}/me/profile`, {
        method: "GET",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });

      if (res.status === 404) {
        // No profile yet — not an error, just first time
        setProfile(null);
      } else if (!res.ok) {
        const userMessage =
          res.status === 401
            ? "Tu sesión expiró. Por favor, volvé a iniciar sesión."
            : "No se pudo cargar el perfil. Intentá de nuevo más tarde.";
        setError(userMessage);
      } else {
        const body = (await res.json()) as { profile: Partial<HealthProfile> };
        setProfile(body.profile ?? null);
        setError(undefined);
      }
    } catch (err) {
      const message =
        err instanceof Error
          ? err.message
          : "Error de conexión. Intentá de nuevo.";
      setError(message);
    } finally {
      setLoading(false);
    }
  }, [getIdToken]);

  // Fetch profile on mount
  useEffect(() => {
    void refetch();
  }, [refetch]);

  const save = useCallback(
    async (partial: Partial<HealthProfile>) => {
      const token = await getIdToken();
      if (!token) {
        setError("No autenticado.");
        return;
      }

      setLoading(true);
      setSaved(false);
      setError(undefined);

      try {
        const res = await fetch(`${workerUrlRef.current}/me/profile`, {
          method: "PUT",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify(partial),
        });

        if (!res.ok) {
          // 422 validation error or 5xx — show generic message (PII-safe)
          const userMessage =
            res.status === 401
              ? "Tu sesión expiró. Por favor, volvé a iniciar sesión."
              : res.status === 422
                ? "Algunos datos del perfil no son válidos. Revisá los campos marcados."
                : "No se pudo guardar el perfil. Intentá de nuevo más tarde.";
          setError(userMessage);
        } else {
          setSaved(true);
          // Refresh from server to get server-side fields (updatedAt, profileSource)
          await refetch();
        }
      } catch (err) {
        setError("Error de conexión. Intentá de nuevo.");
      } finally {
        setLoading(false);
      }
    },
    [getIdToken, refetch],
  );

  // exactOptionalPropertyTypes: conditional spread for optional fields
  return {
    profile,
    loading,
    saved,
    ...(error !== undefined ? { error } : {}),
    save,
    refetch,
  };
}
